import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Info } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import Title from '@/components/ui/Title';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import CamLayoutSelectors from '@/components/pages/live/CamLayoutSelectors';
import CamLayoutPreview from '@/components/pages/live/CamLayoutPreview';
import { updateWorkspace } from '@/features/workspace/workspaceThunks';
import {
  DEFAULT_CAM_LAYOUT,
  MAX_GRID_PRESENTERS,
  camLayoutCapacity,
  fitCamLayout,
  fromApiCamLayout,
  toApiCamLayout,
  toCamLayoutSelection,
  type CamLayoutConfig,
} from '@/lib/camLayouts';

/**
 * Disposition caméras par défaut du workspace, appliquée à ses nouveaux lives (et modifiable
 * pendant le live depuis la régie). L'aperçu se joue avec un nombre de présentateurs libre pour
 * voir la réduction de taille et la bascule au-delà de la capacité.
 */
export default function CamLayoutSection() {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const currentWorkspace = useAppSelector((state) => state.workspace.currentWorkspace);
  const savedConfig = fromApiCamLayout(currentWorkspace?.camLayout) ?? DEFAULT_CAM_LAYOUT;
  // Choix en cours d'enregistrement : il s'affiche tout de suite et cède la place à la valeur du
  // workspace dès que l'API a répondu, qu'elle ait accepté ou non.
  const [pendingConfig, setPendingConfig] = useState<CamLayoutConfig | null>(null);
  /** Numéro du dernier enregistrement lancé : une réponse plus ancienne ne décide plus de rien. */
  const saveSequenceRef = useRef(0);
  const [presenterCount, setPresenterCount] = useState(1);
  const config = pendingConfig ?? savedConfig;

  const capacity = camLayoutCapacity(config);
  const applied = fitCamLayout(config, presenterCount);
  const appliedSelection = toCamLayoutSelection(applied);
  const isAdapted = applied !== config;

  const handleChange = async (next: CamLayoutConfig) => {
    // Le fuseau est réécrit à chaque enregistrement : tant qu'on ne connaît pas sa valeur réelle,
    // enregistrer reviendrait à l'écraser.
    if (!currentWorkspace?.timezone) {
      toast.error(t('camLayout.saveError'));
      return;
    }
    setPendingConfig(next);
    const sequence = (saveSequenceRef.current += 1);
    const result = await dispatch(
      updateWorkspace({
        workspaceId: currentWorkspace.id,
        input: {
          name: currentWorkspace.name,
          timezone: currentWorkspace.timezone,
          promoCode: currentWorkspace.promoCode,
          camLayout: toApiCamLayout(next),
        },
      }),
    );
    if (sequence !== saveSequenceRef.current) return;
    setPendingConfig(null);
    if (updateWorkspace.rejected.match(result)) {
      toast.error(t('camLayout.saveError'));
    }
  };

  return (
    <section className="space-y-4">
      <div>
        <Title level={2}>{t('camLayout.title')}</Title>
        <p className="text-sm text-tertiary">{t('camLayout.description')}</p>
      </div>

      <CamLayoutSelectors value={config} onChange={handleChange} />

      <p className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-tertiary">
        <Info className="size-4 shrink-0" aria-hidden />
        <span>
          {t('camLayout.capacityInfo', { count: capacity })}
          {isAdapted &&
            ` ${t('camLayout.previewAdapted', {
              count: presenterCount,
              position: tCommon(`camLayout.position_${appliedSelection.position}`),
              size: tCommon(`camLayout.size_${appliedSelection.size}`),
              format: tCommon(`camLayout.format_${appliedSelection.format}`),
            })}`}
        </span>
      </p>

      <div className="flex items-center justify-between gap-4">
        <span className="text-base font-medium">{t('camLayout.previewCount')}</span>
        <Select
          value={String(presenterCount)}
          onValueChange={(value) => setPresenterCount(Number(value))}
        >
          <SelectTrigger className="h-10 w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: MAX_GRID_PRESENTERS }, (_, i) => i + 1).map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <span className="text-base font-medium">{t('camLayout.preview')}</span>
        <CamLayoutPreview
          config={config}
          presenterCount={presenterCount}
          className="mx-auto max-w-[720px] overflow-hidden rounded-lg"
        />
      </div>
    </section>
  );
}
